import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { Package, Plus, Search, Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  categories as categoryApi,
  products,
  type AdminCategory,
  type AdminProduct,
} from '../api/endpoints'
import { Button } from '../components/Button'
import { Card, CardContent } from '../components/Card'
import { DataTable } from '../components/DataTable'
import { Input } from '../components/Input'
import { Dialog, DialogContent, DialogClose } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { formatDate, formatINR } from '../lib/format'
import { cn } from '../lib/cn'

type Tab = 'products' | 'categories'

export function ProductsPage() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('products')

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="flex items-end justify-between gap-3 border-b border-line">
        <div className="flex gap-1">
          <TabButton active={tab === 'products'} onClick={() => setTab('products')}>
            Products
          </TabButton>
          <TabButton active={tab === 'categories'} onClick={() => setTab('categories')}>
            Categories
          </TabButton>
        </div>
        {tab === 'products' ? (
          <Button onClick={() => navigate('/products/new')} size="sm" className="mb-2">
            <Plus className="size-4" />
            New product
          </Button>
        ) : (
          <Button size="sm" className="mb-2" id="new-category-trigger" />
        )}
      </div>

      {tab === 'products' ? <ProductsTab /> : <CategoriesTab />}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition',
        active
          ? 'border-forest text-ink'
          : 'border-transparent text-slatey hover:text-ink hover:border-line',
      )}
    >
      {children}
    </button>
  )
}

// ─── Products tab ────────────────────────────────────────────────

function ProductsTab() {
  const navigate = useNavigate()
  const [data, setData] = useState<AdminProduct[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const limit = 20

  function load() {
    setLoading(true)
    const params: Record<string, string | number | undefined> = { page, limit }
    if (search) params.search = search
    if (category) params.category = category
    products
      .list(params as any)
      .then((r) => {
        setData(r.products)
        setTotal(r.total)
      })
      .catch((err) => toast.error('Failed to load products', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  function handleDelete() {
    if (!deleteSlug) return
    setDeleting(true)
    products
      .delete(deleteSlug)
      .then(() => {
        toast.success('Product deleted')
        setDeleteSlug(null)
        load()
      })
      .catch((err) => toast.error('Failed to delete product', { description: err.message }))
      .finally(() => setDeleting(false))
  }

  const totalPages = Math.ceil(total / limit)

  const columns: ColumnDef<AdminProduct>[] = [
    {
      header: 'Product',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3 max-w-[260px]">
          <div className="size-10 shrink-0 rounded-md bg-ivory border border-line overflow-hidden flex items-center justify-center">
            {row.original.image ? (
              <img src={row.original.image} alt="" className="size-full object-cover" />
            ) : (
              <Package className="size-4 text-slatey" />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{row.original.name}</p>
            <p className="text-xs text-slatey font-mono truncate">{row.original.slug}</p>
          </div>
        </div>
      ),
    },
    {
      header: 'Category',
      accessorKey: 'category.name',
      cell: ({ row }) => (
        <span className="text-xs bg-ivory px-2 py-0.5 rounded-full text-slatey">
          {row.original.category.name}
        </span>
      ),
    },
    {
      header: 'Price',
      accessorKey: 'price',
      cell: ({ row }) => (
        <div className="text-sm">
          <span className="font-medium">{formatINR(row.original.price)}</span>
          {row.original.discount > 0 && (
            <>
              <span className="text-xs text-slatey line-through ml-1">{formatINR(row.original.originalPrice)}</span>
              <span className="text-xs text-forest ml-1">-{row.original.discount}%</span>
            </>
          )}
        </div>
      ),
    },
    {
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex gap-1 flex-wrap">
          {!row.original.isActive && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-danger/10 text-danger font-medium">Inactive</span>
          )}
          {row.original.featured && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-saffron/20 text-amber-dark font-medium">Featured</span>
          )}
          {row.original.bestseller && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-forest/10 text-forest font-medium">Bestseller</span>
          )}
          {row.original.trending && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">Trending</span>
          )}
          {row.original.new && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">New</span>
          )}
        </div>
      ),
    },
    {
      header: 'Sales',
      accessorKey: 'orderCount',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.orderCount} orders</span>
      ),
    },
    {
      header: 'Created',
      accessorKey: 'createdAt',
      cell: ({ getValue }) => (
        <span className="text-xs text-slatey">{formatDate(String(getValue()))}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/products/${row.original.slug}`)
            }}
            className="size-7 rounded text-slatey hover:text-ink hover:bg-ivory inline-flex items-center justify-center"
            title="Edit"
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setDeleteSlug(row.original.slug)
            }}
            className="size-7 rounded text-slatey hover:text-danger hover:bg-danger/10 inline-flex items-center justify-center"
            title="Delete"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <>
      {/* Filters */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-3">
            <div className="w-full sm:w-72 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slatey" />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1)
                    load()
                  }
                }}
                className="pl-9"
              />
            </div>
            <Input
              placeholder="Category ID"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1)
                  load()
                }
              }}
              className="w-full sm:w-48"
            />
            <Button variant="outline" size="sm" onClick={() => { setPage(1); load() }}>
              Search
            </Button>
            {(search || category) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSearch(''); setCategory(''); setPage(1) }}
              >
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        emptyTitle="No products yet"
        emptyDescription="Add your first product to start selling."
        emptyAction={
          <Button onClick={() => navigate('/products/new')}>
            <Plus className="size-4" /> Add product
          </Button>
        }
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-slatey">
            Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
          </p>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!deleteSlug} onOpenChange={(open) => !open && setDeleteSlug(null)}>
        <DialogContent
          title="Delete product"
          description="This cannot be undone. All associated data will be permanently removed."
        >
          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button variant="destructive" loading={deleting} onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ─── Categories tab ─────────────────────────────────────────────

function CategoriesTab() {
  const [data, setData] = useState<AdminCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<AdminCategory | null>(null)
  const [creating, setCreating] = useState(false)

  function reload() {
    setLoading(true)
    categoryApi
      .list()
      .then((r) => setData(r.categories))
      .catch((err) => toast.error('Failed to load categories', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  async function onDelete(c: AdminCategory) {
    if (!confirm(`Delete category "${c.name}"?`)) return
    try {
      await categoryApi.delete(c.id)
      toast.success('Category deleted')
      reload()
    } catch (err) {
      toast.error('Delete failed', { description: (err as Error).message })
    }
  }

  const columns: ColumnDef<AdminCategory>[] = [
    {
      header: 'Name',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3 max-w-[280px]">
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{row.original.name}</p>
            <p className="text-xs text-slatey font-mono truncate">{row.original.slug}</p>
          </div>
        </div>
      ),
    },
    {
      header: 'Products',
      accessorKey: 'productCount',
      cell: ({ getValue }) => <span className="text-xs text-slatey">{getValue<number>()}</span>,
    },
    {
      header: 'Sort',
      accessorKey: 'sortOrder',
      cell: ({ getValue }) => <span className="text-xs text-slatey">{getValue<number>()}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(row.original) }}
            className="size-7 rounded text-slatey hover:text-ink hover:bg-ivory inline-flex items-center justify-center"
            title="Edit"
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(row.original) }}
            className="size-7 rounded text-slatey hover:text-danger hover:bg-danger/10 inline-flex items-center justify-center"
            title="Delete"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} size="sm">
          <Plus className="size-4" /> New category
        </Button>
      </div>
      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        emptyTitle="No categories yet"
        emptyDescription="Create categories to organize your products."
        emptyAction={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" /> New category
          </Button>
        }
      />
      {creating ? (
        <CategoryDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            reload()
          }}
        />
      ) : null}
      {editing ? (
        <CategoryDialog
          category={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      ) : null}
    </>
  )
}

function CategoryDialog({
  category,
  onClose,
  onSaved,
}: {
  category?: AdminCategory
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!category
  const [name, setName] = useState(category?.name ?? '')
  const [slug, setSlug] = useState(category?.slug ?? '')
  const [slugDirty, setSlugDirty] = useState(isEdit)
  const [sortOrder, setSortOrder] = useState(String(category?.sortOrder ?? 0))
  const [saving, setSaving] = useState(false)

  function autoSlug(s: string) {
    return s
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 120)
  }

  async function onSave() {
    if (!name.trim() || !slug.trim()) {
      toast.error('Name and slug are required')
      return
    }
    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        slug: slug.trim(),
        sortOrder: parseInt(sortOrder, 10) || 0,
      }
      if (isEdit && category) {
        await categoryApi.update(category.id, payload)
        toast.success('Category updated')
      } else {
        await categoryApi.create(payload)
        toast.success('Category created')
      }
      onSaved()
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={isEdit ? 'Edit category' : 'New category'}
        description={
          isEdit
            ? 'Update this category. Slug must remain unique.'
            : 'Create a new product category. Slug must be unique.'
        }
      >
        <div className="space-y-4">
          <FormField label="Name" required>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (!slugDirty) setSlug(autoSlug(e.target.value))
              }}
              required
            />
          </FormField>
          <FormField label="Slug" required hint="Lowercase letters, digits, hyphens.">
            <Input
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value)
                setSlugDirty(true)
              }}
              placeholder="category-slug"
            />
          </FormField>
          <FormField label="Sort order">
            <Input
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </FormField>
          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button onClick={onSave} loading={saving}>
              {isEdit ? 'Save changes' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}